import { QueryClient, QueryCache, MutationCache } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { ERROR_MESSAGES } from '@/constants'

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      console.error('Query error:', error)
      
      // Show toast only for critical queries
      if (query.meta?.showError !== false) {
        const msg = query.meta?.errorMessage || (error instanceof Error ? error.message : ERROR_MESSAGES.GENERIC)
        toast.error(msg as string)
      }
    }
  }),
  mutationCache: new MutationCache({
    onSuccess: (_data, _variables, _context, mutation) => {
      if (mutation.meta?.successMessage) {
        toast.success(mutation.meta.successMessage as string)
      }
    },
    onError: (error, _variables, _context, mutation) => {
      console.error('Mutation error:', error)
      
      if (mutation.meta?.showError !== false) {
        const msg = mutation.meta?.errorMessage || (error instanceof Error ? error.message : ERROR_MESSAGES.GENERIC)
        toast.error(msg as string)
      }
    }
  }),
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes (formerly cacheTime)
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      networkMode: 'offlineFirst'
    },
    mutations: {
      retry: 2,
      retryDelay: 1000,
      networkMode: 'offlineFirst'
    }
  }
})

// Query key factories
export const queryKeys = {
  auth: {
    all: ['auth'] as const,
    user: () => [...queryKeys.auth.all, 'user'] as const,
    profile: (uid: string) => [...queryKeys.auth.all, 'profile', uid] as const
  },
  inquiries: {
    all: ['inquiries'] as const,
    lists: () => [...queryKeys.inquiries.all, 'list'] as const,
    list: (filters: Record<string, unknown>) => [...queryKeys.inquiries.lists(), filters] as const,
    details: () => [...queryKeys.inquiries.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.inquiries.details(), id] as const,
    stats: () => [...queryKeys.inquiries.all, 'stats'] as const
  },
  installations: {
    all: ['installations'] as const,
    lists: () => [...queryKeys.installations.all, 'list'] as const,
    list: (filters: Record<string, unknown>) => [...queryKeys.installations.lists(), filters] as const,
    details: () => [...queryKeys.installations.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.installations.details(), id] as const,
    timeline: (id: string) => [...queryKeys.installations.detail(id), 'timeline'] as const
  },
  tickets: {
    all: ['tickets'] as const,
    lists: () => [...queryKeys.tickets.all, 'list'] as const,
    list: (filters: Record<string, unknown>) => [...queryKeys.tickets.lists(), filters] as const,
    details: () => [...queryKeys.tickets.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.tickets.details(), id] as const,
    comments: (id: string) => [...queryKeys.tickets.detail(id), 'comments'] as const
  },
  followups: {
    all: ['followups'] as const,
    lists: () => [...queryKeys.followups.all, 'list'] as const,
    list: (filters: Record<string, unknown>) => [...queryKeys.followups.lists(), filters] as const,
    details: () => [...queryKeys.followups.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.followups.details(), id] as const
  },
  engineers: {
    all: ['engineers'] as const,
    lists: () => [...queryKeys.engineers.all, 'list'] as const,
    list: () => [...queryKeys.engineers.lists()] as const,
    details: () => [...queryKeys.engineers.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.engineers.details(), id] as const,
    performance: (id: string) => [...queryKeys.engineers.detail(id), 'performance'] as const
  },
  dashboard: {
    all: ['dashboard'] as const,
    kpis: () => [...queryKeys.dashboard.all, 'kpis'] as const,
    stats: () => [...queryKeys.dashboard.all, 'stats'] as const
  },
  notifications: {
    all: ['notifications'] as const,
    lists: () => [...queryKeys.notifications.all, 'list'] as const,
    unread: () => [...queryKeys.notifications.all, 'unread'] as const
  },
  reports: {
    all: ['reports'] as const,
    engineerPerformance: () => [...queryKeys.reports.all, 'engineerPerformance'] as const
  }
}

export default queryClient
